<script lang="ts">
  import type { AnalysisSeries, AnalysisSummary } from '../lib/analysis';
  import type { EventDetailsJobResult, EventModelJobResult } from '../lib/event-analysis-computation';
  import type { AnalysisDataMode } from '../lib/types';
  import type { AnalysisSummaryRow, CorrelationLookup } from './analysis-panel-types';

  export let open: boolean;
  export let dataMode: AnalysisDataMode;
  export let selectedSeries: AnalysisSeries[];
  export let domainsCompatible: boolean;
  export let eventDetailsPending: boolean;
  export let eventDetailsError: string;
  export let eventStatistics: EventDetailsJobResult['eventStatistics'];
  export let alignmentStatistics: EventDetailsJobResult['alignmentStatistics'];
  export let powerResults: EventModelJobResult['powerResults'];
  export let balanceResults: EventModelJobResult['balanceResults'];
  export let summaries: AnalysisSummaryRow[];
  export let correlationCell: CorrelationLookup;
  export let onOpenChange: (open: boolean) => void;

  function formatValue(value: number | null, maximumSignificantDigits = 5): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return new Intl.NumberFormat('en-GB', { maximumSignificantDigits, useGrouping: true }).format(value);
  }

  function trend(summary: AnalysisSummary, series: AnalysisSeries): string {
    const value = formatValue(summary.trendPerX);
    if (value === '—') return value;
    return `${value}${series.unit ? ` ${series.unit}` : ''}${summary.trendUnit}`;
  }

  function measurement(value: number | null, unit = ''): string {
    if (value == null || !Number.isFinite(value)) return formatValue(value);
    const formatted = formatValue(value);
    return !unit ? formatted : `${formatted} ${unit}`;
  }
</script>

<details class="analysis-details" on:toggle={(event) => onOpenChange((event.currentTarget as HTMLDetailsElement).open)}>
  <summary>Analysis details</summary>
  {#if open}
  <div class="analysis-details__content">
    {#if dataMode === 'event-waveforms'}
      <section class="analysis-results" aria-labelledby="event-waveform-statistics-heading">
        <h3 id="event-waveform-statistics-heading">Event waveform statistics</h3>
        {#if eventDetailsPending}
          <p class="analysis-help" role="status">Calculating waveform statisticsâ€¦</p>
        {:else if eventDetailsError}
          <p class="analysis-help" role="alert">{eventDetailsError}</p>
        {:else if eventStatistics.length}
        <div class="analysis-table-wrap">
          <table>
            <thead><tr><th scope="col">Series</th><th scope="col">Duration</th><th scope="col">Gaps</th><th scope="col">RMS</th><th scope="col">Peak-to-peak</th><th scope="col">DC offset</th><th scope="col">Crest factor</th><th scope="col">Max rate</th><th scope="col">Fundamental</th><th scope="col">Fundamental phase</th><th scope="col">THD</th><th scope="col">Dominant non-fundamental</th><th scope="col">Energy above 500 Hz</th><th scope="col">Pre-trigger RMS</th><th scope="col">Post-trigger RMS</th><th scope="col">Absolute RMS change</th><th scope="col">RMS change</th></tr></thead>
            <tbody>
              {#each eventStatistics as item (item.series.id)}
                <tr>
                  <th scope="row">{item.series.label}</th>
                  <td>{measurement(item.statistics.durationMs, 'ms')}</td>
                  <td>{item.statistics.gaps.toLocaleString('en-GB')}</td>
                  <td>{measurement(item.statistics.rms, item.series.unit)}</td>
                  <td>{measurement(item.statistics.peakToPeak, item.series.unit)}</td>
                  <td>{measurement(item.statistics.dcOffset, item.series.unit)}</td>
                  <td>{formatValue(item.statistics.crestFactor)}</td>
                  <td>{measurement(item.statistics.maximumRate, `${item.series.unit}/ms`)}</td>
                  <td>{measurement(item.statistics.fundamentalFrequencyHz, 'Hz')}</td>
                  <td>{measurement(item.statistics.fundamentalPhaseDegrees, 'deg')}</td>
                  <td>{measurement(item.statistics.thdPercent, '%')}</td>
                  <td>{measurement(item.statistics.dominantNonFundamentalHz, 'Hz')}</td>
                  <td>{measurement(item.statistics.highFrequencyEnergyPercent, '%')}</td>
                  <td>{measurement(item.statistics.preTriggerRms, item.series.unit)}</td>
                  <td>{measurement(item.statistics.postTriggerRms, item.series.unit)}</td>
                  <td>{measurement(item.statistics.rmsChangeAbsolute, item.series.unit)}</td>
                  <td>{measurement(item.statistics.rmsChangePercent, '%')}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
        {/if}
      </section>
    {/if}

    {#if alignmentStatistics.length}
      <section class="analysis-results" aria-labelledby="waveform-alignment-statistics-heading">
        <h3 id="waveform-alignment-statistics-heading">Waveform alignment statistics</h3>
        <div class="analysis-table-wrap">
          <table>
            <thead><tr><th scope="col">Series pair</th><th scope="col">Best lag</th><th scope="col">Maximum correlation</th><th scope="col">Matched samples</th><th scope="col">Aligned RMSE</th><th scope="col">Maximum residual</th></tr></thead>
            <tbody>
              {#each alignmentStatistics as item (`${item.left.id}:${item.right.id}`)}
                <tr>
                  <th scope="row">{item.left.label} vs {item.right.label}</th>
                  <td>{measurement(item.lagMs, 'ms')}</td>
                  <td>{formatValue(item.correlation, 4)}</td>
                  <td>{item.matchedSamples.toLocaleString('en-GB')}</td>
                  <td>{measurement(item.rmse, item.left.unit)}</td>
                  <td>{measurement(item.maximumResidual, item.left.unit)}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>
    {/if}

    {#if powerResults.length}
      <section class="analysis-results" aria-labelledby="calculated-power-statistics-heading">
        <h3 id="calculated-power-statistics-heading">Calculated power statistics</h3>
        <div class="analysis-table-wrap">
          <table>
            <thead><tr><th scope="col">Series</th><th scope="col">Phase</th><th scope="col">Real power</th><th scope="col">Apparent power</th><th scope="col">Fundamental reactive power</th><th scope="col">Power factor</th><th scope="col">Peak absolute power</th><th scope="col">Captured energy</th></tr></thead>
            <tbody>
              {#each powerResults as result (result.series.id)}
                <tr>
                  <th scope="row">{result.series.label}</th>
                  <td>{result.phase}</td>
                  <td>{measurement(result.statistics.realPowerKw, 'kW')}</td>
                  <td>{measurement(result.statistics.apparentPowerKva, 'kVA')}</td>
                  <td>{measurement(result.statistics.fundamentalReactivePowerKvar, 'kvar')}</td>
                  <td>{formatValue(result.statistics.powerFactor)}</td>
                  <td>{measurement(result.statistics.peakAbsolutePowerKw, 'kW')}</td>
                  <td>{measurement(result.statistics.energyWh, 'Wh')}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>
    {/if}

    {#if balanceResults.length}
      <section class="analysis-results" aria-labelledby="three-phase-balance-statistics-heading">
        <h3 id="three-phase-balance-statistics-heading">Three-phase balance statistics</h3>
        <div class="analysis-table-wrap">
          <table>
            <thead><tr><th scope="col">Series</th><th scope="col">Quantity</th><th scope="col">Mean negative sequence</th><th scope="col">Maximum negative sequence</th><th scope="col">Mean zero sequence</th><th scope="col">Maximum zero sequence</th></tr></thead>
            <tbody>
              {#each balanceResults as result (result.negativeSequence.id)}
                <tr>
                  <th scope="row">{result.negativeSequence.label}</th>
                  <td>{result.quantity}</td>
                  <td>{measurement(result.statistics.meanNegativeSequencePercent, '%')}</td>
                  <td>{measurement(result.statistics.maximumNegativeSequencePercent, '%')}</td>
                  <td>{measurement(result.statistics.meanZeroSequencePercent, '%')}</td>
                  <td>{measurement(result.statistics.maximumZeroSequencePercent, '%')}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>
    {/if}

    <section class="analysis-results" aria-labelledby="summary-statistics-heading">
      <h3 id="summary-statistics-heading">Summary statistics</h3>
      <div class="analysis-table-wrap">
        <table>
          <thead><tr><th scope="col">Series</th><th scope="col">Unit</th><th scope="col">Samples</th><th scope="col">Mean</th><th scope="col">Std dev</th><th scope="col">Min</th><th scope="col">Median</th><th scope="col">Max</th><th scope="col">P05</th><th scope="col">P95</th><th scope="col">Linear trend</th></tr></thead>
          <tbody>
            {#each summaries as item (item.series.id)}
              <tr>
                <th scope="row">{item.series.label}</th>
                <td>{item.series.unit || '—'}</td>
                <td>{item.summary.count.toLocaleString('en-GB')}</td>
                <td>{formatValue(item.summary.mean)}</td>
                <td>{formatValue(item.summary.standardDeviation)}</td>
                <td>{formatValue(item.summary.minimum)}</td>
                <td>{formatValue(item.summary.median)}</td>
                <td>{formatValue(item.summary.maximum)}</td>
                <td>{formatValue(item.summary.percentile05)}</td>
                <td>{formatValue(item.summary.percentile95)}</td>
                <td>{trend(item.summary, item.series)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </section>

    {#if selectedSeries.length >= 2 && domainsCompatible}
      <section class="analysis-results" aria-labelledby="correlation-matrix-heading">
        <h3 id="correlation-matrix-heading">Full-range correlation</h3>
        <div class="analysis-table-wrap">
          <table class="correlation-matrix">
            <thead><tr><th scope="col">Series</th>{#each selectedSeries as series (series.id)}<th scope="col">{series.label}</th>{/each}</tr></thead>
            <tbody>
              {#each selectedSeries as left (left.id)}
                <tr>
                  <th scope="row">{left.label}</th>
                  {#each selectedSeries as right (right.id)}
                    {@const cell = correlationCell(left, right)}
                    <td title={`${cell.matchedSamples.toLocaleString('en-GB')} matched samples`}>{formatValue(cell.value, 4)}</td>
                  {/each}
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </section>
    {/if}
  </div>
  {/if}
</details>
